/**
 * 日本語日記（characterData.description_ja）の出自・手動編集履歴（descriptionMeta）を扱う
 * 純粋ユーティリティ。React / Firestore SDK には依存せず、FieldValue は呼び出し側から注入する。
 */
import type { DescriptionMetadata } from '../types';

export type { DescriptionMetadata } from '../types';

/** Firestore ドキュメント上のフィールドパス（updateDoc のキーとして使用） */
export const DESCRIPTION_META_FIELDS = {
  descriptionJa: 'characterData.description_ja',
  source: 'descriptionMeta.source',
  editCount: 'descriptionMeta.editCount',
  lastEditedAt: 'descriptionMeta.lastEditedAt',
  updatedAt: 'updatedAt',
} as const;

/**
 * 新規保存時の初期メタデータ。AI生成の日記が実在するときだけ作成し、
 * 存在しない場合は undefined（原文を捏造しない）。
 */
export const createInitialDescriptionMeta = (
  aiDescriptionJa: string | null | undefined
): DescriptionMetadata | undefined => {
  if (typeof aiDescriptionJa !== 'string' || aiDescriptionJa.trim().length === 0) return undefined;
  return {
    source: 'ai',
    editCount: 0,
  };
};

/** Owner-only payload. Never persist this under the publicly readable cards collection. */
export const createPrivateDiaryProvenance = (
  aiDescriptionJa: string | null | undefined
): { originalAiDescription: string } | undefined => {
  if (typeof aiDescriptionJa !== 'string' || aiDescriptionJa.trim().length === 0) return undefined;
  return { originalAiDescription: aiDescriptionJa };
};

const toEditCount = (v: unknown): number =>
  typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.floor(v) : 0;

const isDescriptionSource = (v: unknown): v is DescriptionMetadata['source'] =>
  v === 'ai' || v === 'user-edited';

const isTimestampValue = (v: unknown): v is NonNullable<DescriptionMetadata['lastEditedAt']> =>
  (v instanceof Date && !Number.isNaN(v.getTime())) ||
  (typeof v === 'string' && !Number.isNaN(Date.parse(v))) ||
  (!!v && typeof v === 'object' && typeof (v as { toDate?: unknown }).toDate === 'function');

/**
 * Firestore ドキュメントの生値から descriptionMeta を安全に取り出す。
 * レガシーカード（フィールド無し）や不正値は undefined（後方互換）。
 * lastEditedAt は Firestore Timestamp / Date / 文字列のいずれもそのまま保持する。
 */
export const parseDescriptionMeta = (raw: unknown): DescriptionMetadata | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;
  const o = raw as Record<string, unknown>;
  if (!isDescriptionSource(o.source)) return undefined;
  const meta: DescriptionMetadata = { source: o.source, editCount: toEditCount(o.editCount) };
  if (isTimestampValue(o.lastEditedAt)) meta.lastEditedAt = o.lastEditedAt;
  return meta;
};

/**
 * 手動編集後のローカル状態を返す（Firestore 側の increment / serverTimestamp と対応）。
 * - source は user-edited、editCount は +1（未存在なら 0 起点 → 1）
 * - AI原文は所有者専用ドキュメントに分離されているため、この公開状態には含めない
 */
export const applyLocalDescriptionEdit = (
  current: DescriptionMetadata | null | undefined,
  editedAt: Date = new Date()
): DescriptionMetadata => {
  const next: DescriptionMetadata = {
    source: 'user-edited',
    editCount: toEditCount(current?.editCount) + 1,
    lastEditedAt: editedAt,
  };
  return next;
};

/** Firestore の FieldValue 生成関数（firebase/firestore の increment / serverTimestamp と同じ形） */
export interface DescriptionEditFieldValues<TIncrement = unknown, TTimestamp = unknown> {
  increment: (n: number) => TIncrement;
  serverTimestamp: () => TTimestamp;
}

export type DescriptionEditUpdate<TIncrement, TTimestamp> = {
  readonly [DESCRIPTION_META_FIELDS.descriptionJa]: string;
  readonly [DESCRIPTION_META_FIELDS.source]: 'user-edited';
  readonly [DESCRIPTION_META_FIELDS.editCount]: TIncrement;
  readonly [DESCRIPTION_META_FIELDS.lastEditedAt]: TTimestamp;
  readonly [DESCRIPTION_META_FIELDS.updatedAt]: TTimestamp;
};

/**
 * 手動編集を Firestore に反映する updateDoc 用ペイロード。
 * ドット区切りのフィールドパスで部分更新するため、既存の他フィールドは上書きされない。
 * 所有者専用のAI原文ドキュメントには触れない。descriptionMeta が無いレガシーカードでも
 * increment(1) により editCount は 1 から始まる。
 */
export const buildDescriptionEditUpdate = <TIncrement, TTimestamp>(
  editedDescriptionJa: string,
  fieldValues: DescriptionEditFieldValues<TIncrement, TTimestamp>
): DescriptionEditUpdate<TIncrement, TTimestamp> => ({
  [DESCRIPTION_META_FIELDS.descriptionJa]: editedDescriptionJa,
  [DESCRIPTION_META_FIELDS.source]: 'user-edited',
  [DESCRIPTION_META_FIELDS.editCount]: fieldValues.increment(1),
  [DESCRIPTION_META_FIELDS.lastEditedAt]: fieldValues.serverTimestamp(),
  [DESCRIPTION_META_FIELDS.updatedAt]: fieldValues.serverTimestamp(),
});
